import { useRef, type KeyboardEvent } from 'react';

import { Icon } from '../../components/Icon';
import { isVectorSource } from '../../data/model';
import type { InspectorTab } from '../state/appStore';
import { useAppStore } from '../state/useAppStore';
import { LabelsInspector } from './labels/LabelsInspector';
import { PopupsInspector } from './popups/PopupsInspector';
import { StyleInspector } from './style/StyleInspector';
import { DataInspector } from './data/DataInspector';
import { effectiveVectorSourceForLayer } from '../../joins/effectiveLayer';
import { AnnotationInspector } from './annotations/AnnotationInspector';

const INSPECTOR_TABS: readonly { id: InspectorTab; label: string }[] = [
  { id: 'style', label: 'Style' },
  { id: 'labels', label: 'Labels' },
  { id: 'popups', label: 'Popups' },
  { id: 'data', label: 'Data' },
];

export function Inspector() {
  const tabRefs = useRef(new Map<InspectorTab, HTMLButtonElement>());
  const project = useAppStore((state) => state.project);
  const selectedLayerId = useAppStore((state) => state.shell.selectedLayerId);
  const activeAnnotationLayerId = useAppStore((state) => state.shell.activeAnnotationLayerId);
  const activeTab = useAppStore((state) => state.shell.activeInspectorTab);
  const closeInspector = useAppStore((state) => state.closeInspector);
  const setActiveTab = useAppStore((state) => state.setActiveInspectorTab);
  const layer = project.layers.find((candidate) => candidate.id === selectedLayerId);
  const source = layer
    ? project.sources.find((candidate) => candidate.id === layer.sourceId)
    : undefined;

  if (!selectedLayerId && activeAnnotationLayerId) return <AnnotationInspector />;
  if (!layer || !source || !isVectorSource(source)) return null;
  const effectiveSource = effectiveVectorSourceForLayer(project.sources, layer) ?? source;

  const closeAndRestoreFocus = () => {
    const layerControl = document.getElementById(`layer-select-${layer.id}`);
    closeInspector();
    requestAnimationFrame(() => layerControl?.focus());
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tab: InspectorTab) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = INSPECTOR_TABS.findIndex((candidate) => candidate.id === tab);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? INSPECTOR_TABS.length - 1
          : event.key === 'ArrowLeft'
            ? (currentIndex - 1 + INSPECTOR_TABS.length) % INSPECTOR_TABS.length
            : (currentIndex + 1) % INSPECTOR_TABS.length;
    const nextTab = INSPECTOR_TABS[nextIndex];
    if (!nextTab) return;
    setActiveTab(nextTab.id);
    tabRefs.current.get(nextTab.id)?.focus();
  };

  return (
    <aside className="inspector" aria-label={`Inspector for ${layer.name}`}>
      <div className="inspector-heading">
        <div>
          <h2>{layer.name}</h2>
        </div>
        <button
          aria-label="Close Inspector"
          className="icon-button"
          onClick={closeAndRestoreFocus}
          title="Close Inspector"
          type="button"
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="inspector-tabs" role="tablist" aria-label="Layer Inspector">
        {INSPECTOR_TABS.map((tab) => (
          <button
            aria-controls={`inspector-panel-${tab.id}`}
            aria-selected={activeTab === tab.id}
            className={
              activeTab === tab.id ? 'inspector-tab inspector-tab-active' : 'inspector-tab'
            }
            id={`inspector-tab-${tab.id}`}
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => handleTabKeyDown(event, tab.id)}
            ref={(element) => {
              if (element) tabRefs.current.set(tab.id, element);
              else tabRefs.current.delete(tab.id);
            }}
            role="tab"
            tabIndex={activeTab === tab.id ? 0 : -1}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        aria-labelledby={`inspector-tab-${activeTab}`}
        className="inspector-panel"
        id={`inspector-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'style' ? <StyleInspector layer={layer} source={effectiveSource} /> : null}
        {activeTab === 'labels' ? <LabelsInspector layer={layer} source={effectiveSource} /> : null}
        {activeTab === 'popups' ? <PopupsInspector layer={layer} source={effectiveSource} /> : null}
        {activeTab === 'data' ? <DataInspector layer={layer} source={source} /> : null}
      </div>
    </aside>
  );
}
